import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import './Legal.css';

const LAST_UPDATED = '2026-08-30';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Screen>
      <div className="top">
        <div className="legal">
          <div className="legal__header">
            <button
              type="button"
              className="legal__back"
              onClick={() => navigate(-1)}
              aria-label={t('back', { defaultValue: 'Back' })}
            >
              <Icon name="angle-left" />
            </button>
            <h1 className="legal__title">{t('privacyPolicy', { defaultValue: 'Privacy Policy' })}</h1>
          </div>

          <section className="legal__hero">
            <div className="legal__hero-kicker">Tornea</div>
            <h2 className="legal__hero-title">
              {t('privacyPolicy', { defaultValue: 'Privacy Policy' })}
            </h2>
            <div className="legal__hero-meta">
              {t('lastUpdated', { defaultValue: 'Last updated' })}: {LAST_UPDATED}
            </div>
          </section>

          <article className="legal__body">
            <p>
              This Privacy Policy explains what personal data Tornea ("we", "us") collects when you use the Tornea
              app and event platform, how we use it, and the choices you have. We aim to keep this short and plain.
            </p>

            <h2>1. Who we are</h2>
            <p>
              The Tornea app and event platform (the "Service") is run by two companies of the same group
              (together "Tornea", "we", "us"):
            </p>
            <ul>
              <li>
                <strong>Herculean CV</strong> — VAT BE 0830.631.784, Liersesteenweg 240A/7, 2220 Heist-op-den-Berg,
                Belgium. Responsible for your data if you take part in an event or community in the European
                Economic Area.
              </li>
              <li>
                <strong>Hercules Trophy Middle East FZ LLC</strong> — licence 2705/2012, Creative Tower,
                Hamad Bin Abdullah, P.O. Box 4422, Fujairah, United Arab Emirates. Responsible for events and
                communities everywhere else.
              </li>
            </ul>
            <p>
              For any privacy question, either way: <a href="mailto:info@tornea.net">info@tornea.net</a>.
            </p>
            <p>
              We decide how the Service handles your data. The organizer or business unit whose event or community
              you take part in decides what happens within that event — which content is shown, who is invited, and
              what follows a report (see sections 4 and 5).
            </p>

            <h2>2. Information we collect</h2>
            <ul>
              <li><strong>Account info</strong> — name, email, password (hashed), language, optional profile photo.</li>
              <li><strong>Event participation data</strong> — your team, company, role, scores, missions, votes, awards, and challenges within an event you join.</li>
              <li><strong>Content you submit</strong> — photos, social-media posts, comments, cheers, bios, and team slogans.</li>
              <li><strong>Reports and moderation records</strong> — content you report (the reason you pick and any note you write), reports other people file about content you posted, and any posting restriction or account suspension (its reason, scope, duration, and who decided).</li>
              <li><strong>Usage and device data</strong> — log entries, IP address, device type, browser, and timestamps for security and debugging.</li>
              <li><strong>Cookies</strong> — a session cookie for authentication and a small set of preferences (e.g., language, branding domain).</li>
            </ul>

            <h2>3. How we use it</h2>
            <ul>
              <li>To run events: leaderboards, team pages, mission submissions, voting, and notifications.</li>
              <li>To keep your account secure and recover it if needed.</li>
              <li>To communicate operational messages (account verification, password resets, event updates).</li>
              <li>To review reported content and enforce our <Link to="/terms">Terms</Link> — removing content, restricting posting, or suspending an account.</li>
              <li>To improve the Service: anonymized analytics, debugging, and abuse prevention.</li>
            </ul>

            <h2>4. Sharing</h2>
            <p>
              Within an event you participate in, basic profile info (name, photo, team) is visible to other
              participants and on public team pages. We do <strong>not</strong> sell personal data. We share data
              with third parties only when:
            </p>
            <ul>
              <li>The organizer or business unit whose event or community you take part in — their back-office team sees your participation data, the content posted there, and any report filed about it, including the name of the person who filed it.</li>
              <li>A service provider (hosting, email delivery, image storage) acts on our behalf under contract.</li>
              <li>You explicitly request it (e.g., posting to a social network).</li>
              <li>Required by law or to protect rights, safety, or the integrity of the Service.</li>
            </ul>

            <h2>5. Reporting and moderation</h2>
            <p>
              Wherever the Service lets people post — event walls, company feeds, photo albums — it also lets people
              report a post or a photo. Reporting is not the same as deleting: a report removes nothing by itself.
              It opens an item in the back office of the business unit that runs that event or community, where a
              person decides what to do. The exception is a photo takedown request, which removes the picture
              immediately.
            </p>
            <ul>
              <li>
                <strong>A report is not anonymous.</strong> We record who reported, who wrote or uploaded the
                reported content, the reason chosen, any note added, and the event or company it happened on. The
                back-office team of that business unit sees all of it, including the reporter's name.
              </li>
              <li>
                <strong>A report outlives the content.</strong> Reports are kept after the post or photo is gone, so
                that a repeated pattern can be recognized.
              </li>
              <li>
                <strong>Enforcement is recorded.</strong> If someone is stopped from posting or their account is
                suspended, we store the reason, who decided, when it starts, when it ends, and when it was lifted. A
                restriction can be limited to one business unit or apply across the platform.
              </li>
              <li>
                <strong>People are told.</strong> The author is notified when their content is removed, the person
                who reported is notified of the outcome, and anyone restricted receives an email with the reason.
              </li>
              <li>
                <strong>Decisions are made by people.</strong> We do not screen, score, or remove content
                automatically, and no moderation decision is taken by an algorithm alone.
              </li>
            </ul>
            <p>
              We process this data to keep the Service safe and usable and to meet our obligations as a hosting
              provider — in EU terms, our legitimate interest and a legal obligation. You can object to this
              processing, and ask us to review any decision taken about your content or your account, at{' '}
              <a href="mailto:info@tornea.net">info@tornea.net</a>.
            </p>

            <h2>6. Storage and security</h2>
            <p>
              Data is stored on servers operated by us or our hosting providers, with reasonable technical and
              organizational measures (encryption in transit, access controls, hashed passwords). No system is
              perfectly secure; please use a strong, unique password.
            </p>
            <p>
              We work from Belgium and the United Arab Emirates, with organizers in several countries, so your data
              may be stored in, accessed from, or transferred to a country other than your own — including outside
              the European Economic Area. Where such a transfer needs a legal safeguard, such as the European
              Commission's standard contractual clauses, we put one in place.
            </p>

            <h2>7. Retention</h2>
            <p>
              We keep account data while your account is active and for a reasonable period after deletion to
              comply with legal obligations and resolve disputes. Event data may be retained as part of the event
              record (leaderboards, awards) after an event ends.
            </p>
            <p>
              Reports and moderation records are kept beyond the content they are about — normally up to 24 months
              after a report is closed or a restriction is lifted, and longer where we need them to establish,
              exercise, or defend a legal claim.
            </p>

            <h2>8. Your rights</h2>
            <p>
              Depending on your jurisdiction, you may have the right to access, correct, export, or delete your
              personal data, restrict or object to processing, and lodge a complaint with a supervisory authority.
              To exercise any of these, email <a href="mailto:info@tornea.net">info@tornea.net</a>.
            </p>
            <p>
              If you are in the European Economic Area, you can also complain to your national data-protection
              authority. For Herculean CV that is the Belgian Data Protection Authority
              (Gegevensbeschermingsautoriteit), Drukpersstraat 35, 1000 Brussels.
            </p>

            <h2>9. Children</h2>
            <p>
              The Service is not intended for children under 13 (or the equivalent minimum age in your country).
              If you believe a child has provided us with personal data, please contact us so we can remove it.
            </p>

            <h2>10. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. The "Last updated" date at the top will reflect any
              changes. Material changes will be communicated through the app or via email where appropriate.
            </p>

            <h2>11. Contact</h2>
            <p>
              Questions, requests, or concerns: <a href="mailto:info@tornea.net">info@tornea.net</a>.
            </p>

            <Link to="/terms" className="legal__footer-link">
              {t('termsOfService', { defaultValue: 'Terms of Service' })}
              <Icon name="angle-right" />
            </Link>
          </article>
        </div>
      </div>

      <div className="bottom">
        <NavBar />
      </div>
    </Screen>
  );
};

export default PrivacyPolicy;
